import Loading from '@/components/Loading/Loading';
import SendCrossDomainResults from '@/components/Team/AdminConsole/InVivoSendValidator/SendCrossDomainResults';
import SendResultsGrid from '@/components/Team/AdminConsole/InVivoSendValidator/SendResultsGrid';
import { findBundleValidationResult } from '@/components/Team/AdminConsole/InVivoSendValidator/sendResults';
import { _notNil } from '@/littledash';
import type { AgentSessionApiId, AgentSessionV1 } from '@/model/Agent.model';
import { useApiHook } from '@/support/Hooks/api/useApiHook';
import { ModalActions, ModalContainer, ModalHeader } from '@/utils/modal';
import { type FC, useMemo } from 'react';
import styles from './AgentSendResults.module.scss';

interface AgentSendResultsProps {
  /** The SEND session whose validation result to fetch and render. */
  sessionApiId: AgentSessionApiId;
  /** Injected by the modal system; closes the modal. */
  closeModal: () => void;
}

/**
 * Modal that renders a SEND bundle validation result client-side. Opened from a `send_result` tree
 * node's "View results" action, which supplies only the session identity — the typed
 * `SendBundleValidationResult` lives on the session's validation execution, not the node, so the
 * modal fetches the session and reads the result off it via {@link findBundleValidationResult}.
 *
 * Deliberately unlike {@link AgentReport}: the SEND result is NOT server-rendered HTML. The server
 * returns the typed findings and this modal styles them with the same {@link SendCrossDomainResults}
 * and {@link SendResultsGrid} components the admin-console screen uses. Shows a spinner while loading,
 * an inline error on failure or when no result is present, and the two-tier findings otherwise.
 */
const AgentSendResults: FC<AgentSendResultsProps> = ({ sessionApiId, closeModal }) => {
  const { response, loading } = useApiHook({
    endpoint: 'GET /api/v1/ai-agents/sessions/{sessionApiId}',
    path: { sessionApiId },
    options: {
      onError: {
        throw: false,
        preserveOriginalError: false,
        toast: false,
        capture: true,
        slug: 'agent-send-results-load',
      },
    },
  });

  const result = useMemo(
    () => (response?.type === 'success' ? findBundleValidationResult(response.body as AgentSessionV1) : null),
    [response]
  );

  if (loading) {
    return <Loading txt="Loading results" />;
  }

  return (
    <ModalContainer size="wide" testKey="agent-send-results-modal">
      <ModalHeader title="SEND validation results" closeModal={closeModal} className="pa3 bb b--moon-gray bg-white" />
      <div className={styles.body} data-testid="agent-send-results-content">
        {response?.type === 'error' ? (
          <p className="gray tc" data-test-element="error">
            Couldn't load the validation results.
          </p>
        ) : _notNil(result) ? (
          <div className="flex flex-column" data-test-element="results">
            <SendCrossDomainResults
              groups={result.cross_domain}
              summary={result.summary}
              truncation={result.truncation}
            />
            {result.per_domain.map((perDomain) => (
              <div key={perDomain.domain_code} className="mt3" data-test-element="per-domain-result">
                <SendResultsGrid result={perDomain} testId={`agent-send-results__results--${perDomain.domain_code}`} />
              </div>
            ))}
          </div>
        ) : (
          <p className="gray tc" data-test-element="empty-state">
            No validation results.
          </p>
        )}
      </div>
      <div className="pa3 bt b--moon-gray">
        <ModalActions onCancel={closeModal} cancelBtnText="Close" />
      </div>
    </ModalContainer>
  );
};

export default AgentSendResults;
